import { useState, useEffect } from "react";
import axios from "../../api/axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import styles from './Register.module.css';
import { Snackbar } from "@mui/material";
import Alert from '@mui/material/Alert';
import { useDispatch } from "react-redux";
import { setCredentials } from "../../redux/authSlice";

const USER_REGEX = /^[A-Za-z][A-Za-z0-9.\s-]{4,29}$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{5,24}$/;
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const AUM_REGEX = /^\d{1,4}$/;
const REGISTER_URL = '/users';

const Register = () => {

    const dispatch = useDispatch();

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const surveyFromUrl = searchParams.get("survey");
    console.log(`surveyFromUrl: ${surveyFromUrl}`);

    const qualtricsSurveyId = surveyFromUrl || sessionStorage.getItem("qualtricsSurveyId");
    console.log(`qualtricsSurveyId: ${qualtricsSurveyId}`);

    const [user, setUser] = useState('');
    const [validName, setValidName] = useState(false);
    const [userFocus, setUserFocus] = useState(false);

    const [pwd, setPwd] = useState('');
    const [validPwd, setValidPwd] = useState(false);
    const [pwdFocus, setPwdFocus] = useState(false);

    const [matchPwd, setMatchPwd] = useState('');
    const [validMatch, setValidMatch] = useState(false);
    const [matchFocus, setMatchFocus] = useState(false);

    const [email, setEmail] = useState('');
    const [validEmail, setValidEmail] = useState(false);
    const [emailFocus, setEmailFocus] = useState(false);

    const [aum, setAum] = useState('');
    const [validAum, setValidAum] = useState(false);
    const [aumFocus, setAumFocus] = useState(false);

    // const [success, setSuccess] = useState(false);

    const [showPasswords, setShowPasswords] = useState(false);

    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [alertStatus, setAlertStatus] = useState({
        open: false
    });

    const isExternalUrl = (url) => {
        try {
            const urlObj = new URL(url, window.location.origin);
            return urlObj.origin !== window.location.origin;
        } catch {
            return false;
        }
    };

    const getRelativePath = (inputUrl) => {

        const origin = window.location.origin;
        const homepage = process.env.PUBLIC_URL || '';

        try {
            const url = new URL(inputUrl);

            if (!inputUrl.startsWith(origin)) {
                console.warn('URL does not match current origin');
                return '/';
            }

            let relativePath = url.pathname;
            if (homepage && relativePath.startsWith(homepage)) {
                relativePath = relativePath.substring(homepage.length);
            }

            return relativePath + url.search + url.hash;
        } catch (error) {
            console.error('Invalid URL:', error);
            return '';
        }
    }

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertStatus((prev) => {
            return { ...prev, open: false }
        });
    };

    useEffect(() => {
        setValidName(USER_REGEX.test(user));
    }, [user]);

    useEffect(() => {
        setValidEmail(EMAIL_REGEX.test(email));
    }, [email]);

    useEffect(() => {
        setValidPwd(PWD_REGEX.test(pwd));
        setValidMatch(pwd === matchPwd);
    }, [pwd, matchPwd]);

    useEffect(() => {
        setValidAum(AUM_REGEX.test(aum));
    }, [aum]);

    useEffect(() => {
        setSubmitDisabled(!validName || !validEmail || !validPwd || !validMatch || !validAum);
    }, [validName, validEmail, validPwd, validMatch, validAum]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const v1 = USER_REGEX.test(user);
        const v2 = PWD_REGEX.test(pwd);
        const v3 = EMAIL_REGEX.test(email);
        const v4 = AUM_REGEX.test(aum);
        if (!v1 || !v2 || !v3 || !v4) {
            return;
        }
        try {
            const response = await axios.post(REGISTER_URL,
                JSON.stringify({
                    name: user,
                    email: email,
                    password: pwd,
                    aum: parseInt(aum),
                    qualtricsSurveyId
                }),
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true
                }
            );

            console.log(JSON.stringify(response?.data));

            const accessToken = response?.data?.accessToken;
            const isAdmin = response?.data?.isAdmin;
            const userId = response?.data?.userId;
            const surveyUrl = response?.data?.surveyUrl;
            const showBenchmarkingTour = response?.data?.showBenchmarkingTour;
            const showBenchmarkingHint = response?.data?.showBenchmarkingHint;
            const showInsightsTour = response?.data?.showInsightsTour;
            const profileComplete = response?.data?.profileComplete;
            dispatch(setCredentials({ email, userId, isAdmin, accessToken, showBenchmarkingTour, showBenchmarkingHint, showInsightsTour, profileComplete }));

            const redirectPath = sessionStorage.getItem("redirectAfterAuth");
            sessionStorage.removeItem("redirectAfterAuth");
            sessionStorage.removeItem("qualtricsSurveyId");

            if (surveyUrl) {
                console.log(`surveyUrl: ${surveyUrl} isExternalUrl: ${isExternalUrl(surveyUrl)} `);
                if (isExternalUrl(surveyUrl)) {
                	window.location.href = surveyUrl;
                } else {
                    const relativePath = getRelativePath(surveyUrl);
                    console.log(`relativePath: ${relativePath}`);
                	navigate(relativePath);
                }
            } else if (redirectPath) {
                navigate(redirectPath);
            } else {
                navigate("/");
            }

        } catch (err) {
            let message;
            if (!err?.response) {
                message = 'Error: No Server Response';
            } else if (err.response?.status === 409) {
                message = 'That email address is already registered';
            } else {
                message = 'Error: Registration Failed';
            }
            setIsLoading(false);
            setAlertStatus({
                open: true,
                severity: "error",
                message: message
            });
        }
    };

    return (
        <section className={styles.section}>
            <h1>Register</h1>
            <form onSubmit={handleSubmit}>
                <div className={styles.inputWrapper}>
                    <label htmlFor="name">Name:</label>
                    <input
                        type="text"
                        id="name"
                        className={(validName || !user) ? "" : styles.invalid}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        onChange={(e) => setUser(e.target.value)}
                        value={user}
                        required
                        aria-invalid={validName ? "false" : "true"}
                        aria-describedby="usernote"
                        onFocus={() => setUserFocus(true)}
                        onBlur={() => setUserFocus(false)}
                    />
                    <p
                        id="usernote"
                        className={userFocus && user && !validName ? styles.tooltip : styles.offscreen}
                    >
                        4 to 30 characters.<br />
                        Must begin with a letter.<br />
                        Letters, numbers, hyphens, and periods allowed.
                    </p>
                </div>

                <div className={styles.inputWrapper}>
                    <label htmlFor="email">Email:</label>
                    <input
                        type="email"
                        id="email"
                        className={(validEmail || !email) ? "" : styles.invalid}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        onChange={(e) => setEmail(e.target.value)}
                        value={email}
                        required
                        aria-invalid={validEmail ? "false" : "true"}
                        aria-describedby="emailnote"
                        onFocus={() => setEmailFocus(true)}
                        onBlur={() => setEmailFocus(false)}
                    />
                    <p
                        id="emailnote"
                        className={emailFocus && email && !validEmail ? styles.tooltip : styles.offscreen}
                    >
                        Must be a valid email address.
                    </p>
                </div>

                <div className={styles.inputWrapper}>
                    <label htmlFor="aum">Assets Under Management ($millions):</label>
                    <input
                        type="text"
                        id="aum"
                        className={(validAum || !aum) ? styles.currency : styles.invalidCurrency}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        onChange={(e) => setAum(e.target.value)}
                        value={aum}
                        required
                        aria-invalid={validAum ? "false" : "true"}
                        aria-describedby="aumnote"
                        onFocus={() => setAumFocus(true)}
                        onBlur={() => setAumFocus(false)}
                        maxLength={4}
                    />
                    <p
                        id="aumnote"
                        className={aumFocus && aum && !validAum ? styles.tooltip : styles.offscreen}
                    >
                        In $millions<br />Must be a whole number between 0 and 9,999.
                    </p>
                </div>

                <div className={styles.inputWrapper}>
                    <label htmlFor="password">Password:</label>
                    <input
                        type={showPasswords ? "text" : "password"}
                        id="password"
                        className={(validPwd || !pwd) ? "" : styles.invalid}
                        onChange={(e) => setPwd(e.target.value)}
                        value={pwd}
                        required
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        aria-invalid={validPwd ? "false" : "true"}
                        aria-describedby="pwdnote"
                        onFocus={() => setPwdFocus(true)}
                        onBlur={() => setPwdFocus(false)}
                    />
                    <p
                        id="pwdnote"
                        className={pwdFocus && !validPwd ? styles.tooltip : styles.offscreen}
                    >
                        8 to 24 characters.<br />
                        Must include uppercase and lowercase letters, a number and a special character.<br />
                        Allowed special characters: <span aria-label="exclamation mark">!</span> <span aria-label="at symbol">@</span> <span aria-label="hashtag">#</span> <span aria-label="dollar sign">$</span> <span aria-label="percent">%</span>
                    </p>
                </div>

                <div className={styles.inputWrapper}>
                    <label htmlFor="confirm_pwd">Confirm Password:</label>
                    <input
                        type={showPasswords ? "text" : "password"}
                        id="confirm_pwd"
                        className={(validMatch || !matchPwd) ? "" : styles.invalid}
                        onChange={(e) => setMatchPwd(e.target.value)}
                        value={matchPwd}
                        required
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        aria-invalid={validMatch ? "false" : "true"}
                        aria-describedby="confirmnote"
                        onFocus={() => setMatchFocus(true)}
                        onBlur={() => setMatchFocus(false)}
                    />
                    <p
                        id="confirmnote"
                        className={matchFocus && !validMatch ? styles.tooltip : styles.offscreen}
                    >
                        Must match the first password
                    </p>
                </div>

                <div className={styles.checkboxAndLabel}>
                    <input
                        type="checkbox"
                        id="password_visibility"
                        name="password_visibility"
                        // value="false"
                        onChange={(e) => setShowPasswords(e.target.checked)}
                    />
                    <label htmlFor="password_visibility">Show passwords</label>
                </div>

                <button
                    className={styles.submitButton}
                    disabled={submitDisabled || isLoading}
                >
                    {isLoading ? "Signing Up..." : "Sign Up"}
                </button>
            </form>
            <nav>
                <Link to="/login">Sign in</Link>
            </nav>
            <Snackbar open={alertStatus.open} autoHideDuration={6000} onClose={handleClose}>
                <Alert onClose={handleClose} variant="filled" severity={alertStatus.severity} sx={{ width: '100%' }}>{alertStatus.message}</Alert>
            </Snackbar>

        </section>

    );
};

export default Register;